import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollReveal } from '@/hooks/useReveal';
import { fetchPodcasts, mediaUrl } from '@/lib/api';

function extractYoutubeId(url) {
    if (!url) return null;
    const match = String(url).match(
        /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/i,
    );
    return match?.[1] ?? null;
}

function resolveVideoSource(episode) {
    const type = episode.videoType;
    const url = episode.videoURL;

    if (type === 'file') {
        return url ? { kind: 'file', src: url } : null;
    }

    if (type === 'youtube') {
        const id = extractYoutubeId(url);
        return id ? { kind: 'embed', src: `https://www.youtube.com/embed/${id}` } : null;
    }

    // Direct video URL — also tolerate legacy YouTube links saved as "url"
    if (!url) return null;
    const youtubeId = extractYoutubeId(url);
    if (youtubeId) {
        return { kind: 'embed', src: `https://www.youtube.com/embed/${youtubeId}` };
    }
    const vimeo = String(url).match(/vimeo\.com\/(\d+)/i);
    if (vimeo) {
        return { kind: 'embed', src: `https://player.vimeo.com/video/${vimeo[1]}` };
    }
    return { kind: 'file', src: url };
}

function VideoPlayer({ episode, autoPlay = false }) {
    const source = resolveVideoSource(episode);
    if (!source) {
        return (
            <div className="rounded-xl border border-border/50 bg-muted px-4 py-8 text-center text-sm text-muted-foreground">
                Video coming soon
            </div>
        );
    }

    if (source.kind === 'embed') {
        const embedSrc = autoPlay
            ? `${source.src}${source.src.includes('?') ? '&' : '?'}autoplay=1&rel=0&playsinline=1`
            : source.src;
        return (
            <div className="relative w-full overflow-hidden rounded-xl bg-black aspect-video">
                <iframe
                    src={embedSrc}
                    title={episode.title}
                    className="absolute inset-0 h-full w-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            </div>
        );
    }

    return (
        <video
            key={mediaUrl(source.src)}
            controls
            playsInline
            autoPlay={autoPlay}
            poster={episode.imageURL ? mediaUrl(episode.imageURL) : undefined}
            className="w-full rounded-xl bg-black aspect-video object-contain"
            src={mediaUrl(source.src)}
        >
            Your browser does not support the video tag.
        </video>
    );
}

function EpisodeCard({ episode, index, onWatch }) {
    const staggerClass = `stagger-${Math.min(index + 1, 6)}`;

    return (
        <div
            className={`reveal-up group flex flex-col rounded-2xl overflow-hidden border transition-all duration-500 hover:shadow-xl hover:-translate-y-1 ${staggerClass}`}
            style={{
                background: 'var(--card)',
                borderColor: 'var(--border)',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            }}
        >
            <button
                type="button"
                onClick={() => onWatch(episode)}
                className="relative overflow-hidden text-left w-full"
                style={{ aspectRatio: '16/9' }}
                aria-label={`Play ${episode.title}`}
            >
                <img
                    src={mediaUrl(episode.imageURL) || '/assets/images/swaranjali-melb.jpg'}
                    alt={episode.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 60%)' }} />
                <div className="absolute top-3 left-3 flex items-center gap-2">
                    <span
                        className="px-2.5 py-1 rounded-full text-xs font-bold tracking-widest uppercase"
                        style={{ background: 'linear-gradient(135deg, #ebb413, #f0c030)', color: '#0f0d0a' }}
                    >
                        S{episode.season} E{episode.episode}
                    </span>
                </div>
                <div className="absolute top-3 right-3">
                    <span
                        className="px-2.5 py-1 rounded-full text-xs font-semibold"
                        style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
                    >
                        {episode.theme}
                    </span>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                    <span
                        className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110"
                        style={{ background: 'linear-gradient(135deg, #ebb413, #f0c030)' }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="#0f0d0a">
                            <path d="M8 5v14l11-7z" />
                        </svg>
                    </span>
                </div>
                {episode.duration ? (
                    <div className="absolute bottom-3 right-3">
                        <span className="text-xs font-medium text-white/80 flex items-center gap-1">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <path strokeLinecap="round" d="M12 6v6l4 2" />
                            </svg>
                            {episode.duration}
                        </span>
                    </div>
                ) : null}
            </button>

            <div className="flex flex-col flex-1 p-5 gap-4">
                <div className="flex flex-col gap-1">
                    <p className="section-label">{episode.guest}</p>
                    <h3 className="font-display font-semibold text-base leading-snug group-hover:text-primary transition-colors duration-300" style={{ color: 'var(--foreground)' }}>
                        {episode.title}
                    </h3>
                    {episode.guestTitle ? (
                        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{episode.guestTitle}</p>
                    ) : null}
                </div>

                {episode.description ? (
                    <p className="text-sm leading-relaxed line-clamp-3" style={{ color: 'var(--muted-foreground)' }}>
                        {episode.description}
                    </p>
                ) : null}

                {(episode.tags ?? []).length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                        {episode.tags.map((tag) => (
                            <span key={tag} className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
                                {tag}
                            </span>
                        ))}
                    </div>
                ) : null}

                <div className="mt-auto pt-4 border-t flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
                    <span className="text-xs flex items-center gap-1.5" style={{ color: 'var(--muted-foreground)' }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" />
                            <path strokeLinecap="round" d="M16 2v4M8 2v4M3 10h18" />
                        </svg>
                        {episode.date || 'Episode'}
                    </span>
                    <button
                        type="button"
                        onClick={() => onWatch(episode)}
                        className="text-xs font-semibold flex items-center gap-1 transition-colors duration-200 hover:opacity-80"
                        style={{ color: 'var(--primary)' }}
                    >
                        Watch Episode
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
}

function FeaturedEpisode({ episode, onWatch }) {
    if (!episode) return null;

    return (
        <div
            className="reveal-up rounded-2xl overflow-hidden border"
            style={{
                background: 'var(--secondary)',
                borderColor: 'var(--border)',
                boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            }}
        >
            <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="relative overflow-hidden min-h-[280px] bg-black">
                    {episode.videoURL ? (
                        <div className="h-full min-h-[320px] p-3 sm:p-4 flex items-center">
                            <VideoPlayer episode={episode} />
                        </div>
                    ) : (
                        <img
                            src={mediaUrl(episode.imageURL) || '/assets/images/swaranjali-melb.jpg'}
                            alt={episode.title}
                            className="w-full h-full object-cover min-h-[320px]"
                        />
                    )}
                    <div className="absolute top-4 left-4 flex items-center gap-2">
                        <span className="px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase" style={{ background: 'linear-gradient(135deg, #ebb413, #f0c030)', color: '#0f0d0a' }}>
                            Featured
                        </span>
                        <span
                            className="px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase"
                            style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)' }}
                        >
                            S{episode.season} E{episode.episode}
                        </span>
                    </div>
                </div>

                <div className="flex flex-col justify-center p-8 lg:p-10 gap-5">
                    <div>
                        <p className="section-label mb-2">Latest Episode</p>
                        <h2
                            className="font-display font-semibold leading-tight gold-underline mb-4"
                            style={{ fontSize: 'clamp(1.4rem, 2.5vw, 2rem)', color: 'var(--foreground)' }}
                        >
                            {episode.title}
                        </h2>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(235,180,19,0.15)', border: '1.5px solid rgba(235,180,19,0.4)' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ebb413" strokeWidth="1.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                            </svg>
                        </div>
                        <div>
                            <p className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>{episode.guest}</p>
                            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{episode.guestTitle}</p>
                        </div>
                    </div>

                    {episode.description ? (
                        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{episode.description}</p>
                    ) : null}

                    {(episode.tags ?? []).length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                            {episode.tags.map((tag) => (
                                <span key={tag} className="px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
                                    {tag}
                                </span>
                            ))}
                        </div>
                    ) : null}

                    <div className="flex items-center gap-4 flex-wrap">
                        <button
                            type="button"
                            onClick={() => onWatch(episode)}
                            className="btn-gold inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-xs font-bold tracking-widest uppercase"
                        >
                            <span>Watch Now</span>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                            </svg>
                        </button>
                        <span className="text-xs flex items-center gap-1" style={{ color: 'var(--muted-foreground)' }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <path strokeLinecap="round" d="M12 6v6l4 2" />
                            </svg>
                            {[episode.duration, episode.date].filter(Boolean).join(' · ')}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

function VideoModal({ episode, onClose }) {
    useEffect(() => {
        if (!episode) return undefined;
        const onKey = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', onKey);
        };
    }, [episode, onClose]);

    if (!episode) return null;

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-8" role="dialog" aria-modal="true" aria-label={episode.title}>
            <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={onClose} />
            <div className="relative z-10 w-full max-w-5xl">
                <button
                    type="button"
                    onClick={onClose}
                    className="fixed top-5 right-5 z-20 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors p-0 leading-none sm:top-6 sm:right-6"
                    aria-label="Close video"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="block h-[18px] w-[18px] shrink-0" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                <VideoPlayer episode={episode} autoPlay />
                <div className="mt-4 text-center">
                    <p className="text-white font-display font-semibold text-lg">{episode.title}</p>
                    <p className="text-white/60 text-sm mt-1">{episode.guest}{episode.theme ? ` · ${episode.theme}` : ''}</p>
                </div>
            </div>
        </div>
    );
}

export default function PodcastInterviewsContent() {
    const [episodes, setEpisodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTheme, setActiveTheme] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeEpisode, setActiveEpisode] = useState(null);

    useEffect(() => {
        fetchPodcasts()
            .then(setEpisodes)
            .finally(() => setLoading(false));
    }, []);

    useScrollReveal([activeTheme, searchQuery, episodes.length, loading]);

    const themes = useMemo(
        () => ['All', ...new Set(episodes.map((episode) => episode.theme).filter(Boolean))],
        [episodes],
    );

    const featuredEpisode = episodes.find((episode) => episode.featured) ?? episodes[0] ?? null;
    const filteredEpisodes = episodes.filter((episode) => {
        if (featuredEpisode && episode.id === featuredEpisode.id) return false;
        const matchTheme = activeTheme === 'All' || episode.theme === activeTheme;
        const query = searchQuery.trim().toLowerCase();
        const matchSearch = !query
            || episode.title?.toLowerCase().includes(query)
            || episode.guest?.toLowerCase().includes(query)
            || episode.theme?.toLowerCase().includes(query);
        return matchTheme && matchSearch;
    });

    return (
        <>
            <section
                className="relative overflow-hidden pt-16 lg:pt-20"
                style={{ background: 'linear-gradient(135deg, #0f0d0a 0%, #1a1510 50%, #0f0d0a 100%)' }}
                aria-label="Podcast and Videos Hero"
            >
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #ebb413 0%, transparent 70%)' }} />
                    <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full opacity-8" style={{ background: 'radial-gradient(circle, #ebb413 0%, transparent 70%)' }} />
                </div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
                    <div className="max-w-3xl">
                        <p className="section-label mb-4">Awakasha Entertainment</p>
                        <h1 className="font-display font-semibold leading-tight mb-6" style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', color: '#ffffff', lineHeight: 1.15 }}>
                            Podcast <span className="gold-gradient">and Videos</span>
                        </h1>
                        <p className="text-base lg:text-lg leading-relaxed mb-8 max-w-xl" style={{ color: 'rgba(255,255,255,0.65)' }}>
                            Watch conversations with Sri Lanka&apos;s most celebrated artists, musicians, filmmakers, and cultural icons.
                        </p>
                        <div className="flex flex-wrap gap-8 mt-10 pt-8 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                            {[
                                { value: `${episodes.length}+`, label: 'Episodes' },
                                { value: `${themes.length - 1 || 0}`, label: 'Themes' },
                                { value: 'Video', label: 'Format' },
                            ].map((stat) => (
                                <div key={stat.label}>
                                    <p className="font-display font-bold text-2xl" style={{ color: '#ebb413' }}>{stat.value}</p>
                                    <p className="text-xs tracking-widest uppercase" style={{ color: 'rgba(255,255,255,0.45)' }}>{stat.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-20" style={{ background: 'var(--background)' }} aria-label="Featured episode">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-10">
                        <div className="reveal-up">
                            <p className="section-label mb-2">Featured Episode</p>
                            <h2 className="font-display font-semibold text-section-title gold-underline" style={{ color: 'var(--foreground)' }}>
                                Editor&apos;s Pick
                            </h2>
                        </div>
                    </div>

                    {loading ? (
                        <p className="text-muted-foreground">Loading podcasts...</p>
                    ) : featuredEpisode ? (
                        <FeaturedEpisode episode={featuredEpisode} onWatch={setActiveEpisode} />
                    ) : (
                        <div className="rounded-2xl border border-dashed border-border/60 bg-secondary/60 px-6 py-16 text-center">
                            <p className="text-foreground font-medium text-lg">Podcast videos coming soon</p>
                            <p className="text-muted-foreground text-sm mt-2">Upload episodes from the admin Podcast section.</p>
                        </div>
                    )}
                </div>
            </section>

            <section className="py-16 lg:py-20 border-t" style={{ background: 'var(--secondary)', borderColor: 'var(--border)' }} aria-label="All episodes">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-10">
                        <div className="reveal-up">
                            <p className="section-label mb-2">All Episodes</p>
                            <h2 className="font-display font-semibold text-section-title gold-underline" style={{ color: 'var(--foreground)' }}>
                                Browse & Discover
                            </h2>
                        </div>
                        <p className="reveal-fade text-sm" style={{ color: 'var(--muted-foreground)' }}>
                            {filteredEpisodes.length} episode{filteredEpisodes.length !== 1 ? 's' : ''} found
                        </p>
                    </div>

                    <div className="reveal-up mb-10 flex flex-col gap-4">
                        <div className="relative max-w-md">
                            <svg className="absolute left-3.5 top-1/2 -translate-y-1/2" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: 'var(--muted-foreground)' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Search episodes, guests..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="input-gold w-full pl-10 pr-4 py-2.5 rounded-full text-sm"
                            />
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <span className="text-xs font-semibold tracking-widest uppercase flex items-center mr-2" style={{ color: 'var(--muted-foreground)' }}>
                                Theme:
                            </span>
                            {themes.map((theme) => (
                                <button
                                    key={theme}
                                    type="button"
                                    onClick={() => setActiveTheme(theme)}
                                    className={`px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide transition-all duration-200 ${
                                        activeTheme === theme ? 'btn-gold' : 'btn-outline-gold'
                                    }`}
                                >
                                    {theme}
                                </button>
                            ))}
                        </div>
                    </div>

                    {filteredEpisodes.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {filteredEpisodes.map((episode, index) => (
                                <EpisodeCard key={episode.id} episode={episode} index={index} onWatch={setActiveEpisode} />
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-20 gap-4">
                            <p className="font-display font-semibold text-lg" style={{ color: 'var(--foreground)' }}>
                                {loading ? 'Loading...' : 'No episodes found'}
                            </p>
                            {!loading ? (
                                <button
                                    type="button"
                                    onClick={() => { setActiveTheme('All'); setSearchQuery(''); }}
                                    className="btn-outline-gold px-6 py-2.5 rounded-full text-xs font-bold tracking-widest uppercase mt-2"
                                >
                                    Clear Filters
                                </button>
                            ) : null}
                        </div>
                    )}

                    <div className="mt-12 text-center">
                        <Link to="/events" className="btn-gold inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full text-sm font-bold tracking-widest uppercase">
                            <span>Book Your Next Event</span>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                            </svg>
                        </Link>
                    </div>
                </div>
            </section>

            <VideoModal episode={activeEpisode} onClose={() => setActiveEpisode(null)} />
        </>
    );
}
